import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  MISSION_DASHBOARD_STATE_FILTERS,
  type MissionDashboardStateFilter,
} from "@/types/missions"

/**
 * OKS-31 — State-filter chip row above the per-creator collabs panel.
 *
 * Renders one toggle chip per `MISSION_DASHBOARD_STATE_FILTERS` member —
 * `'all'` plus every Collab state, in that order. The alphabet is not
 * enumerated here on purpose: this comment claimed "7 toggle chips" for
 * three tickets after the row had grown past seven, and a count in prose
 * goes stale on exactly the line the next chip ticket edits.
 *
 * Each chip carries its count from `state_counts` (`Posted (6)`) so chip and
 * funnel always agree. `'all'` is the total, not a summand: the per-state
 * chips sum to it.
 *
 * ⚠️ `stateCounts?.[state] ?? 0` means a chip renders `(0)` for a key the
 * backend never sent — indistinguishable from a genuine zero. So a rendered
 * chip is not evidence the prop arrived; the request specs assert the values.
 *
 * Each chip is a `<button aria-pressed>` so screen readers announce
 * the toggle state. The `role="group"` wrapper carries the
 * `aria-label="Filter by collab state"` hint.
 *
 * Click handler mutates `?state=<value>` server-side via partial reload
 * — same URL contract as funnel-bar clicks (chip ↔ bar agree).
 */
interface MissionDashboardStateFilterChipsProps {
  activeState: MissionDashboardStateFilter
  stateCounts: Record<MissionDashboardStateFilter, number>
  onStateChange: (state: MissionDashboardStateFilter) => void
}

export function MissionDashboardStateFilterChips({
  activeState,
  stateCounts,
  onStateChange,
}: MissionDashboardStateFilterChipsProps) {
  const { t } = useTranslation()

  return (
    <div
      role="group"
      aria-label={t("missions.dashboard.state_chips.group_aria")}
      className="flex flex-wrap items-center gap-2"
      data-testid="mission-dashboard-state-filter-chips"
    >
      {MISSION_DASHBOARD_STATE_FILTERS.map((state) => {
        const count = stateCounts?.[state] ?? 0
        const isActive = activeState === state
        return (
          <Button
            key={state}
            type="button"
            variant={isActive ? "default" : "outline"}
            size="sm"
            aria-pressed={isActive}
            onClick={() => onStateChange(state)}
            className={cn("rounded-full", isActive && "shadow-sm")}
            data-testid={`mission-dashboard-state-filter-chip-${state}`}
            data-active={isActive ? "true" : "false"}
          >
            {t(`missions.dashboard.state_chips.${state}`, { count })}
          </Button>
        )
      })}
    </div>
  )
}
